import {
  HTTP_STATUS_MESSAGES,
  NETWORK_ERROR_MESSAGE,
} from "constants/apiErrorMessages";
import {
  HTTP_STATUS,
  isServerErrorStatus,
} from "constants/httpStatusCodes";

export const isPackageManagementApiSuccess = (status) =>
  status === undefined ||
  status === 1 ||
  status === true ||
  status === "success";

const GENERIC_HTTP_STATUS_TEXT = new Set([
  "internal server error",
  "bad gateway",
  "service unavailable",
  "gateway timeout",
  "error",
  "unknown error",
]);

const isGenericHttpStatusText = (message) => {
  if (typeof message !== "string") {
    return true;
  }

  const normalized = message.replace(/^error:\s*/i, "").trim().toLowerCase();

  if (!normalized) {
    return true;
  }

  return GENERIC_HTTP_STATUS_TEXT.has(normalized);
};

export const parseFetchResponseBody = async (response) => {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const isJsonParseErrorMessage = (message) =>
  /is not valid json/i.test(message) ||
  /unexpected token/i.test(message) ||
  /json\.parse/i.test(message);

export const getHttpStatusMessage = (status, fallback = "Request failed") => {
  if (status && HTTP_STATUS_MESSAGES[status]) {
    return HTTP_STATUS_MESSAGES[status];
  }

  if (isServerErrorStatus(status)) {
    return HTTP_STATUS_MESSAGES[HTTP_STATUS.INTERNAL_SERVER_ERROR];
  }

  return fallback;
};

const parseApiPayloadMessage = (data) => {
  const message = data?.message;
  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }

  const detail = data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (!item || typeof item !== "object") return String(item);

        const fieldPath = Array.isArray(item.loc)
          ? item.loc.filter((part) => part !== "body").join(".")
          : "";
        const itemMessage = item.msg || JSON.stringify(item);

        return fieldPath ? `${fieldPath}: ${itemMessage}` : itemMessage;
      })
      .join(", ");
  }

  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }

  const error = data?.error;
  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  return null;
};

export const getApiErrorMessage = (
  data,
  fallback = "Request failed",
  status
) => {
  const payloadMessage = parseApiPayloadMessage(data);

  if (
    payloadMessage &&
    !(isServerErrorStatus(status) && isGenericHttpStatusText(payloadMessage))
  ) {
    return payloadMessage;
  }

  if (status && HTTP_STATUS_MESSAGES[status]) {
    return HTTP_STATUS_MESSAGES[status];
  }

  if (isServerErrorStatus(status)) {
    return HTTP_STATUS_MESSAGES[HTTP_STATUS.INTERNAL_SERVER_ERROR];
  }

  const normalizedFallback = fallback.replace(/^Error:\s*/i, "").trim();
  if (normalizedFallback && !isGenericHttpStatusText(normalizedFallback)) {
    return normalizedFallback;
  }

  return fallback;
};

export const formatApiErrorForDisplay = (
  error,
  fallback = "Request failed"
) => {
  const status = error?.statusCode;
  const message = error?.message;

  if (typeof message !== "string" || !message.trim()) {
    return fallback;
  }

  const cleaned = message.replace(/\s*\(HTTP\s*\d+\)\.?$/i, "").trim();

  if (!cleaned) {
    return fallback;
  }

  if (
    isGenericHttpStatusText(cleaned) ||
    isJsonParseErrorMessage(cleaned)
  ) {
    return getHttpStatusMessage(
      status,
      HTTP_STATUS_MESSAGES[HTTP_STATUS.INTERNAL_SERVER_ERROR] || fallback
    );
  }

  if (/^failed to fetch$/i.test(cleaned) || /network/i.test(cleaned)) {
    return NETWORK_ERROR_MESSAGE;
  }

  return cleaned;
};
